import { timestampToDateText } from '@/helpers/datetime'
import { NO_CARD_IMAGE_URL } from '@/constants'

/**
 * Global-search helpers for the `inventory` block on `/search` (CDP-2508) —
 * the user's OWN cards matching the query.
 *
 * Shape notes, from the live response:
 *   • items are FLAT card documents (not `{ card, listing }`)
 *   • `id` is a STRING
 *   • `created_at` / `updated_at` are unix seconds, not milliseconds
 *   • there is NO `collx_match_id` — an inventory hit cannot be tied back to a
 *     Card Database hit, so the two are independent result sets, each with its
 *     own `totalHits` / `page` / `totalPages`
 *   • each item carries exactly one `batch_id`
 *
 * That last pair is why "In N batches" is derived by GROUPING: the same card
 * owned in three batches comes back as three items, and the only thing tying
 * them together is the card's own identity fields.
 */

/** Unix SECONDS → display date. Guards against a value already in ms. */
export const inventoryDate = (seconds, options = {}) => {
  if (!seconds) return ''

  const ms = Number(seconds) < 1e12 ? Number(seconds) * 1000 : Number(seconds)

  return timestampToDateText(ms, options)
}

/**
 * Identity of the CARD an inventory item represents.
 *
 * No match id in the payload, so this is built from the fields that describe
 * the card itself. Two copies of the same card in different batches share it;
 * two different cards don't. Location, price, status and batch are deliberately
 * excluded — those are what differs BETWEEN copies.
 */
export const inventoryCardKey = (item = {}) => [
  item.set,
  item.number,
  item.player,
  item.subset,
  item.variation,
  item.year,
].map(part => String(part ?? '').trim().toLowerCase()).join('|')

/**
 * Collapse the raw hits into one row per card, carrying every copy.
 *
 * → [{ key, card, items }] in first-seen order (the API's relevance order).
 */
export const groupInventoryHits = (items = []) => {
  const groups = new Map()

  items.forEach((item) => {
    const key = inventoryCardKey(item)
    const group = groups.get(key)

    if (group) {
      group.items.push(item)
      return
    }

    groups.set(key, { key, card: item, items: [item] })
  })

  return [...groups.values()]
}

/**
 * The batch one inventory hit belongs to, named by the SEARCH RESPONSE.
 *
 * The item carries its own batch, so nothing here needs a `/batches` lookup.
 * Two shapes are tolerated because the field is new: an object (`{ id, name }`)
 * or a bare name alongside `batch_id`.
 */
export const hitBatch = (item = {}) => {
  const batch = item.batch ?? item._search_source?.batch

  if (batch && typeof batch === 'object') {
    return { id: batch.id ?? item.batch_id, name: batch.name || '' }
  }

  return { id: item.batch_id, name: typeof batch === 'string' ? batch : '' }
}

/**
 * The distinct batches a group's copies live in, newest first.
 *
 * "Added" is the COPY's `created_at` — when that copy landed in that batch —
 * not the batch's own creation date, which answers a different question.
 *
 * Names come off the hits themselves (`hitBatch`); an unnamed batch falls back
 * to `Batch #<id>` rather than being hidden.
 */
export const batchesForHits = (items = []) => {
  const byBatch = new Map()

  items.forEach((item) => {
    const { id, name } = hitBatch(item)
    if (!id || byBatch.has(id)) return

    byBatch.set(id, {
      id,
      name: name || `Batch #${id}`,
      addedAt: item.created_at || null,
      addedLabel: item.created_at ? `Added: ${inventoryDate(item.created_at, { short: true })}` : '',
      cardId: item.id,
    })
  })

  return [...byBatch.values()].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
}

/**
 * The batches a CATALOG match is owned in — `belongs_to_batches`, built server
 * side from the current user's cards carrying that `collx_match_id`.
 *
 * This is the authoritative answer to "how many batches is this card in": it
 * counts every card, not just the copies that happened to land on this page of
 * inventory hits.
 */
export const catalogBatches = (match = {}) =>
  (match.belongs_to_batches || []).filter(batch => batch?.id).map(batch => ({
    id: batch.id,
    name: batch.name || `Batch #${batch.id}`,
  }))

/**
 * Row heading: "#123 Player Name".
 *
 * Not the API's `title` — that's the marketplace listing title, long enough to
 * be truncated to uselessness in a search row ("2024 Panini Prizm Draft Anthony
 * Gould #SSS-AGD Saturday Star Auto Blue Ice 17/75"). The card database rows
 * beside it lead with number + player, so these match.
 */
export const inventoryCardTitle = (item = {}) => {
  const parts = [item.number && `#${item.number}`, item.player].filter(Boolean)

  return parts.length ? parts.join(' ') : (item.title || item.sku || '')
}

/**
 * Row subheading: year · brand · set, with the parts the SET already contains
 * dropped.
 *
 * `set` is usually the full name — "2014 Bowman", "2023-24 Bowman U Now" — so
 * a naive join reads "2014 Bowman 2014 Bowman". Year and brand are therefore
 * only prepended when the set doesn't already say them, which also keeps the
 * line useful for the rows where `set` is missing entirely.
 */
export const inventoryCardSubtitle = (item = {}) => {
  const set = String(item.set || '')
  const says = (part) => {
    const value = String(part ?? '').trim()

    return !!value && set.toLowerCase().includes(value.toLowerCase())
  }

  return [
    says(item.year) ? '' : item.year,
    says(item.brand) ? '' : item.brand,
    set,
  ].filter(Boolean).join(' ')
}

/**
 * Thumbnails.
 *
 * The inventory block ships `frontImageUrl` / `backImageUrl` — the SAME names
 * the catalog block uses, so both sections read images the same way. (The
 * snake_case fallbacks below are leftovers from before the field landed; they
 * cost nothing and cover a differently-shaped payload.)
 *
 * A missing back arrives as `no-card-image.png`, not as an empty string, and
 * rendering that would stack a gray placeholder BEHIND the real front image —
 * so the placeholder counts as absent and only a genuine second scan produces
 * the offset back thumbnail.
 */
const isPlaceholderImage = (url = '') => !url || url.includes('no-card-image')

export const inventoryCardImages = (item = {}) => {
  const front = item.frontImageUrl || item.front_image_sm || item.front_image || item.image
  const back = item.backImageUrl || item.back_image_sm || item.back_image

  return {
    front: isPlaceholderImage(front) ? NO_CARD_IMAGE_URL : front,
    back: isPlaceholderImage(back) ? '' : back,
  }
}

/**
 * How many batches this card sits in.
 *
 * Prefers an explicit count from the API when one arrives — the grouped copies
 * only ever cover the hits on the CURRENT page, so a card in five batches shows
 * as "3" if only three copies came back. Falls back to the group until then.
 */
export const inventoryBatchCount = (item = {}, grouped = 0) => {
  const fromApi = item.batches_count ?? item.batch_count

  return Number.isFinite(Number(fromApi)) && fromApi !== null ? Number(fromApi) : grouped
}
